import type * as AppStorage from "@/base/async-storage"
import type { Reducer } from "@reduxjs/toolkit"

import {
  readLocalStorageAsync,
  setPhoneNumberPromptStatus,
} from "@/base/async-storage"
import {
  completeSmsVerificationAsync,
  createPhoneNumberInternationalAsync,
  deletePhoneNumberAsync,
  loadActiveUserAsync,
  setNotificationStatusForPhoneNumberAsync,
} from "@/base/farmhq-api"
import { parsePhoneNumberInput } from "@/base/parse-phone-number"
import { isTruthyString } from "@/base/type-guards"
import * as Models from "@/redux/models"
import { createSelector, createSlice, isAnyOf } from "@reduxjs/toolkit"
import { useTranslation } from "react-i18next"
import { Alert, Platform } from "react-native"

import { getIsActiveUserLoadedFromState } from "./requests.reducer"
import type { RootState } from "./root.reducer"

export const PHONE_NUMBERS_PER_USER_MAX = 2

export type NotificationsStatus = "disabled" | "enabled"

export type { ParsedPhoneNumber } from "@/base/parse-phone-number"
export { parsePhoneNumberInput } from "@/base/parse-phone-number"

export function serializePhoneNumber(
  { digits, e164 }: Models.PhoneNumber,
  options: { showCountryName: boolean },
) {
  let serialized: string | undefined
  if (e164) {
    const parsed = parsePhoneNumberInput(e164)
    if (parsed.isValid) {
      serialized = parsed.phoneNumber.nationalFormat
      if (options.showCountryName) {
        serialized = `${parsed.phoneNumber.country}: ${serialized}`
      }
    }
  } else if (
    Boolean(digits) &&
    isTruthyString(digits.countryCode) &&
    isTruthyString(digits.areaCode) &&
    isTruthyString(digits.prefix) &&
    isTruthyString(digits.suffix)
  ) {
    serialized = `${digits.countryCode}-(${digits.areaCode})-${digits.prefix}-${digits.suffix}`
  }
  return serialized ?? e164
}

const { adapter } = Models.phoneNumber

export interface PhoneNumbersState
  extends Models.ModelState<Models.PhoneNumber> {
  errorCode?: "INVALID"
  promptStatus?: AppStorage.StoredValues["phoneNumberPromptStatus"]
}
const initialState: PhoneNumbersState = {
  entities: {},
  ids: [],
}

const slice = createSlice({
  extraReducers: (builder) =>
    builder

      .addCase(readLocalStorageAsync.fulfilled, (state, { payload }) => {
        state.promptStatus = payload.phoneNumberPromptStatus
      })
      .addCase(setPhoneNumberPromptStatus.pending, (state, { meta }) => {
        state.promptStatus = meta.arg
      })
      .addCase(loadActiveUserAsync.fulfilled, (state, { payload }) => {
        adapter.setAll(
          state,
          payload.phoneNumbers.map((phoneNumber) => {
            const serialized = serializePhoneNumber(phoneNumber, {
              showCountryName: false,
            })
            return { ...phoneNumber, serialized }
          }),
        )
      })
      .addCase(
        completeSmsVerificationAsync.fulfilled,
        (state, { meta, payload }) => {
          if (payload.status === "INVALID") {
            state.errorCode = payload.status
          } else {
            adapter.updateOne(state, {
              changes: {
                isVerified: true,
                notificationsEnabled: true,
              },
              id: meta.arg.upnId,
            })
          }
        },
      )
      .addCase(deletePhoneNumberAsync.fulfilled, (state, { meta }) => {
        adapter.removeOne(state, meta.arg.upnId)
      })
      .addCase(
        setNotificationStatusForPhoneNumberAsync.fulfilled,
        (state, { meta }) => {
          adapter.updateOne(state, {
            changes: {
              notificationsEnabled: meta.arg.nextStatus === "enabled",
            },
            id: meta.arg.upnId,
          })
        },
      )
      .addMatcher(
        isAnyOf(createPhoneNumberInternationalAsync.fulfilled),
        (state, { payload }) => {
          adapter.upsertOne(state, {
            ...payload,
            serialized: serializePhoneNumber(payload, {
              showCountryName: false,
            }),
          })
        },
      ),
  initialState,
  name: "userPhoneNumbers ",
  reducers: {},
})

const userPhoneNumbers: Reducer<PhoneNumbersState> = slice.reducer
export default userPhoneNumbers
export function getCanUserAddPhoneNumbers(state: RootState) {
  return Models.phoneNumber.selectTotal(state) < PHONE_NUMBERS_PER_USER_MAX
}

/**
 * Hook to display an alert when the user tries to add more than
 * the allowed number of phone numbers.
 */
export function useTooManyPhoneNumbers() {
  const { t } = useTranslation("phoneNumbers")

  return () => {
    const message: string = t("tooManyPhoneNumbersMessage")
    if (Platform.OS === "web") {
      // eslint-disable-next-line no-alert
      alert(message)
    } else {
      Alert.alert(t("tooManyPhoneNumbersTitle"), message)
    }
  }
}

export const getUserPhoneNumberStatusFromState = createSelector(
  [
    getIsActiveUserLoadedFromState,
    (state: RootState) => state.userPhoneNumbers.promptStatus,
    Models.phoneNumber.selectAll,
  ],
  (isUserLoaded, promptStatus, phoneNumbers) => {
    if (!isUserLoaded) {
      return { status: "LOADING" }
    }
    if (promptStatus === "skipped") {
      return { status: promptStatus }
    }

    if (phoneNumbers.length === 0) {
      return { status: "NO_PHONE_NUMBER" }
    }
    const hasVerifiedNumber = Boolean(
      phoneNumbers.find((phoneNumber) => {
        return phoneNumber.isVerified
      }),
    )
    if (hasVerifiedNumber) {
      return { status: "VERIFIED" }
    }

    const firstUnverified = phoneNumbers.find((phoneNumber) => {
      return !phoneNumber.isVerified
    })
    if (firstUnverified) {
      return {
        status: "UNVERIFIED_PHONE_NUMBER",
        upnId: firstUnverified.id,
      }
    }
    // Shouldn't happen
    return undefined
  },
)
